import { Card } from '@tremor/react';
import type { ReactNode } from 'react';

interface LoadingProps {
  label?: string;
}

export function LoadingCard({ label = 'Loading…' }: LoadingProps) {
  return (
    <Card>
      <div
        role="status"
        aria-live="polite"
        className="flex items-center justify-center py-12 text-sm text-gray-600"
      >
        {label}
      </div>
    </Card>
  );
}

interface ErrorProps {
  error: unknown;
  onRetry?: () => void;
}

export function ErrorCard({ error, onRetry }: ErrorProps) {
  const message =
    error instanceof Error ? error.message : 'Unexpected error fetching data.';
  return (
    <Card>
      <div role="alert" aria-live="assertive" className="space-y-3 py-6">
        <p className="text-sm font-medium text-red-700">Failed to load data</p>
        <p className="text-sm text-gray-700">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        )}
      </div>
    </Card>
  );
}

interface EmptyProps {
  title: string;
  description?: string;
}

export function EmptyCard({ title, description }: EmptyProps) {
  return (
    <Card>
      <div className="space-y-1 py-12 text-center">
        <p className="text-sm font-medium text-gray-700">{title}</p>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
    </Card>
  );
}

interface QueryGuardProps<T> {
  isLoading: boolean;
  error: unknown;
  data: T | undefined;
  loadingLabel?: string;
  onRetry?: () => void;
  children: (data: T) => ReactNode;
}

export function QueryGuard<T>({
  isLoading,
  error,
  data,
  loadingLabel,
  onRetry,
  children,
}: QueryGuardProps<T>) {
  if (isLoading) return <LoadingCard label={loadingLabel} />;
  if (error) return <ErrorCard error={error} onRetry={onRetry} />;
  if (!data) return <EmptyCard title="No data" />;
  return <>{children(data)}</>;
}
